import 'twin.macro';
import { useCountryConfig } from 'queries/user/useCountryConfig';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAIGenerationContext } from 'components/Editor/AIAssist/contexts/AIGenerationContext';
import { AIPanelType } from 'components/Editor/EditorAIWrapper';
import Button from 'components/Buttons/Button';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from 'queries/user/track';
import { useAIUsage } from 'containers/UserSettings/AIUsage/useAIUsage';
import Popover from 'components/Popover';
import { NoMoreCredits } from 'components/Editor/AIAssist/components/AIPromptButton/NoMoreCredits';

interface Props {
  visible?: boolean;
  panelState: AIPanelType;
  setPanelState: (state: AIPanelType) => void;
}
const MIN_CREDITS_NECESSARY = 3;

export function PromptButton({ visible = true, setPanelState }: Props) {
  const { t } = useTranslation(['common']);
  const { hasData, trackingContext } = useAIGenerationContext();
  const posthog = usePostHog();
  const { data: aiUsageData } = useAIUsage();
  const { isFeatureIAEnabled } = useCountryConfig();

  if (!visible || hasData || !isFeatureIAEnabled) {
    return null;
  }

  const creditsAvailable = aiUsageData?.items?.reduce(
    (total, item) => total + (item.usageLimit - item.usageCount),
    0,
  );

  const hasEnoughCredits =
    creditsAvailable === undefined || creditsAvailable >= MIN_CREDITS_NECESSARY;

  function handleClick() {
    posthog?.startSessionRecording(true);
    trackEvent(`${trackingContext}:AIAssist:start`);
    setPanelState('customPrompt');
  }

  function handleMouseDown(e: React.MouseEvent) {
    e.preventDefault();
  }

  if (!hasEnoughCredits) {
    return (
      <Popover
        portal
        placement="bottom-end"
        tw="absolute right-2 bottom-2 z-20"
      >
        <Popover.Button
          small
          outlined
          icon={SparklesIcon}
          onMouseDown={handleMouseDown}
          tw="bg-white"
        >
          {t('ai.createWithAI')}
        </Popover.Button>
        <Popover.Panel disableMargins size="auto">
          <NoMoreCredits />
        </Popover.Panel>
      </Popover>
    );
  }

  return (
    <Button
      small
      outlined
      icon={SparklesIcon}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      tw="absolute right-2 bottom-2 bg-white z-20"
    >
      {t('ai.createWithAI')}
    </Button>
  );
}
